import React, { useCallback, useMemo } from 'react';

import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import { addFeatureFlagAccessed as featureFlagAccessedUFOv2 } from '@atlaskit/react-ufo/feature-flags-accessed';
import { captureException } from '@sentry/react';
import { History } from 'history';

import { useAnalytics } from '@townsquare/analytics';
import { AppState, useAppStateStore } from '@townsquare/app-state-store';
import { CollabContext, type CollaborationContext } from '@townsquare/collab-context';
import { useAsyncEffect } from '@townsquare/hooks';
import {
	setSentryAAID,
	addFeatureFlagAccessed as featureFlagAccessedSentry,
} from '@townsquare/logging/sentry';
import { initBM3 } from '@townsquare/monitoring/bm3';
import { initReactUFO } from '@townsquare/monitoring/react-ufo';
import {
	initUFO,
	addFeatureFlagAccessed as featureFlagAccessedUFOv1,
} from '@townsquare/monitoring/ufo';
import { useOrgContextActions } from '@townsquare/org-context';
import { createOrgScopedPath } from '@townsquare/router/create-route';
import { preloadRouteComponent } from '@townsquare/router/preload';
import type { GlobalHomePageRoute, SiteHomePageRoute } from '@townsquare/router/route-types';
import type { MatchedRoute } from '@townsquare/router/routing-types';
import { userAccessRedirectContextHolder } from '@townsquare/router/user-access';
import { RbacProvider } from '@townsquare/rovo/entitlement';
import { createSearchParamsFromCurrentUrl } from '@townsquare/search-params';
import {
	initialiseConfigFromBootstrapOnce,
	initialiseFeatureGatesFromBootstrapOnce,
} from '@townsquare/stat-sig/bootstrap';
import { initConfigClient } from '@townsquare/stat-sig/config';
import { featureGatesOptions, observabilityFilter } from '@townsquare/stat-sig/options';
import { setupPlatformGates } from '@townsquare/stat-sig/setup-platform';
import { useCurrentUser } from '@townsquare/user-store';
import { User } from '@townsquare/user-store/types';
import { WorkspaceDetails } from '@townsquare/workspace-store/types';

import { routes } from '../router/routes';
import { FullScreenSpaLoader } from '../ui/FullScreenSpaLoader';

/**
 * Normalised data both bootstrap branches flatten into before mounting
 * the shell. The branch-specific work (Relay unmask vs window read,
 * capability checks) happens upstream — the shell only sees plain data.
 *
 * Distinct from `BootstrapVoltPayload` (the wire shape on `window`):
 * `BootstrapVoltPayload` is the inlined Volt-server payload format, with
 * all fields optional for skew tolerance. `BootstrapShellData` is the
 * post-derivation app-internal shape, with fields that the populators
 * have already filtered/precomputed.
 */
export type BootstrapShellData = {
	orgId: string | undefined;
	cloudIds: string[];
	/**
	 * Populator-precomputed `null`-aware view of `cloudIds`, sent through to
	 * `collabContext.orgCloudIds`. Distinguished from `cloudIds: []` so
	 * downstream consumers can tell "we know the list is empty" from "we
	 * never received a list".
	 *
	 * TODO: collapse with `cloudIds` once we confirm no `CollaborationContext`
	 * consumer distinguishes `null` from `[]` for `orgCloudIds`.
	 */
	orgCloudIds: string[] | null;
	workspaceIds: string[];
	workspaceUuids: string[];
	contextCloudId: string | null;
	collabContextSource: {
		cloudIds: readonly string[];
		isMultiSite: boolean;
		hasMultipleUnits: boolean;
	} | null;
	/** Workspace selected by the active context, if any. Used only for its `cloudId` + `uuid`. */
	activeWorkspace: { cloudId: string; uuid: string } | undefined;
	/** Precomputed by the populator (branch-specific capability check). */
	userHasGoalAccess: boolean;
};

export type BootstrapShellChildContext = {
	collabContext: CollaborationContext;
	currentUser: User;
};

type BootstrapShellProps = {
	bootstrap: BootstrapShellData;
	/** Pre-built by the caller via its branch-specific `initWorkspaceFromHome*` action. */
	workspaceDetails: WorkspaceDetails | undefined;
	matchedRoute: MatchedRoute | null;
	history: History<unknown>;
	children: (ctx: BootstrapShellChildContext) => React.ReactNode;
};

function isGlobalRoute(route: MatchedRoute['route']): route is GlobalHomePageRoute {
	return (route as GlobalHomePageRoute | SiteHomePageRoute)?.routeType === 'global';
}

/**
 * Branch-agnostic goals bootstrap side-effects. Lives downstream of either
 * populator (`Bootstrap` or `BootstrapFromVolt`); both flatten their wire data
 * into `BootstrapShellData` before mounting the shell.
 *
 * WHAT BELONGS HERE: anything whose behaviour doesn't depend on data source —
 * org context, collab-context provider, monitoring, analytics, Statsig init,
 * route preload, goal-app-access registration, `AppState.FULLY_INITIALISED`.
 * WHAT BELONGS IN POPULATORS: anything that needs the raw wire data shape
 * (Relay unmask, fragment-ref helpers, window-payload reads).
 *
 * TODO: render-phase side effects (here and in the populators) should move to
 * `useEffect` to play nicely with StrictMode. Out of scope.
 */
export function BootstrapShell(props: BootstrapShellProps): JSX.Element {
	const { bootstrap, workspaceDetails, matchedRoute, history } = props;
	const {
		orgId,
		cloudIds,
		orgCloudIds,
		workspaceIds,
		workspaceUuids,
		contextCloudId,
		collabContextSource,
		activeWorkspace,
		userHasGoalAccess,
	} = bootstrap;

	if (activeWorkspace) {
		userAccessRedirectContextHolder.setGoalAppAccess(activeWorkspace.cloudId, userHasGoalAccess);
	}

	const [{ appState }, { setAppState }] = useAppStateStore();
	const currentUser = useCurrentUser();
	const analytics = useAnalytics();
	const orgContextActions = useOrgContextActions();

	if (orgId) {
		orgContextActions.setOrgContext({
			orgId,
			cloudIds,
			workspaceIds,
			workspaceUuids,
		});

		// This should only trigger for global routes
		if (
			matchedRoute?.match.params.orgIdentifier !== orgId &&
			matchedRoute?.route &&
			isGlobalRoute(matchedRoute.route) &&
			matchedRoute.route.name !== 'not-found'
		) {
			const { path, pathPattern } = matchedRoute.route;
			const queryParams = Object.fromEntries(createSearchParamsFromCurrentUrl());
			history.replace(
				createOrgScopedPath(`${pathPattern}${path}`, orgId, queryParams, matchedRoute.match.params),
			);
		}
	}

	// Register the route table with the redirect helpers. Master called this
	// from Bootstrap (post-auth, once per session). The shell mounts only
	// post-auth (App.AuthCheck is its gate), so this preserves master's
	// timing semantics. Render-phase call — see TODO above for useEffect move.
	userAccessRedirectContextHolder.initUserAccessRedirect(routes);

	const cloudContext = activeWorkspace?.cloudId ?? contextCloudId;

	const collabContext = useMemo<CollaborationContext>(
		() => ({
			goalWorkspace: cloudContext,
			projectWorkspace: cloudContext,
			rovoWorkspace: cloudContext,
			// Skip ARI when no cloudContext rather than emitting `.../null`.
			contextId: cloudContext ? `ari:cloud:townsquare::site/${cloudContext}` : undefined,
			// CollaborationContext expects mutable arrays; voltPayload guards
			// against mutation via `ReadonlyArray`. Copy at the boundary.
			orgCloudIds: orgCloudIds ? [...orgCloudIds] : null,
			numWorkspaces: workspaceIds.length,
			collabContextScoping: {
				collabContextCloudIds: collabContextSource?.cloudIds
					? [...collabContextSource.cloudIds]
					: undefined,
				isMultiSite: collabContextSource?.isMultiSite,
				hasMultipleUnits: collabContextSource?.hasMultipleUnits,
			},
		}),
		[
			cloudContext,
			orgCloudIds,
			collabContextSource?.cloudIds,
			collabContextSource?.isMultiSite,
			collabContextSource?.hasMultipleUnits,
			workspaceIds,
		],
	);

	setSentryAAID(currentUser.accountId);

	const completeInit = useCallback(() => {
		// Initialize monitoring
		initBM3(analytics.getAnalyticsWebClient(), 'townsquare');
		initUFO(analytics.getAnalyticsWebClient(), 'townsquare');
		initReactUFO(analytics.getAnalyticsWebClient(), routes);

		// Set the analytics client ready
		analytics.setCurrentUserAndTenantInfo(
			currentUser.accountId,
			workspaceDetails?.UUID,
			cloudContext ?? undefined,
		);

		analytics.setTopLevelAttributes({ townsquareApp: 'goals' });

		setAppState(AppState.FULLY_INITIALISED);
	}, [analytics, currentUser.accountId, workspaceDetails?.UUID, cloudContext, setAppState]);

	useAsyncEffect(async () => {
		if (orgId) {
			try {
				const statsigGateInitPromise = initialiseFeatureGatesFromBootstrapOnce().then(
					(statsigBootstrapped) => {
						if (statsigBootstrapped) {
							return;
						}

						return FeatureGates.updateUser(
							featureGatesOptions,
							{
								atlassianAccountId: currentUser.accountId,
								tenantId: cloudContext ?? undefined,
								atlassianOrgId: orgId,
							},
							{
								targetApp: 'atlas_web',
								townsquareApp: 'goals',
								...(currentUser.locale ? { locale: currentUser.locale } : {}),
							},
						);
					},
				);

				const statsigExperimentInitPromise = !initialiseConfigFromBootstrapOnce()
					? initConfigClient({
							atlassianAccountId: currentUser.accountId,
							tenantId: cloudContext ?? undefined,
							atlassianOrgId: orgId,
						})
					: Promise.resolve();

				await Promise.all([statsigGateInitPromise, statsigExperimentInitPromise]);
			} catch (e) {
				captureException(e);
			}
		}

		// If we have a matched route and it has a preload function, call it.
		// This will warm up the route for faster navigation.
		if (matchedRoute?.route) {
			preloadRouteComponent(matchedRoute.route);
		}

		setupPlatformGates({
			onFeatureFlagAccessed: (name, value) => {
				if (observabilityFilter(name)) {
					featureFlagAccessedUFOv1(name, value);
					featureFlagAccessedUFOv2(name, value);
					featureFlagAccessedSentry(name, value);
				}
			},
		});
		completeInit();
	}, [currentUser.accountId, currentUser.locale, completeInit, matchedRoute?.route]);

	if (appState === AppState.LOADING) {
		return <FullScreenSpaLoader />;
	}

	return (
		<CollabContext.Provider value={collabContext}>
			<RbacProvider workspaceUuid={activeWorkspace?.uuid} app="goals">
				{props.children({ collabContext, currentUser })}
			</RbacProvider>
		</CollabContext.Provider>
	);
}
